import { useCallback, useEffect, useRef, useState } from "react";
import { luvut } from "./luvut";
import { api, ApiError, type Liite } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import { onKuva, Pienoiskuva } from "./KuvaIkkuna";
import { kuvanNimi, KUVALAJIT } from "./kuvanimi";

/* ═══════════════════════════════════════════════════════════
   Liitteet.

   Yksi komponentti, jota käytetään kaikkialla: pätevyystodistukset,
   kuormakirjat, tarkastuspöytäkirjat. Kohde annetaan taulun nimenä
   ja id:nä — sama kuvio kuin palvelinpuolella.
   ═══════════════════════════════════════════════════════════ */

export default function Liitteet({
  kohdeTaulu, kohdeId, saaMuokata, saaPoistaa = saaMuokata, otsikko = "Liitteet",
  arkaluonteinenOletus = false, voiMerkitaArkaluonteiseksi = false, muuttui, lisaaTeksti, vihje, poistaTeksti, pienoiskuvat = false,
  lajit, kamera = false, iso = false, paatoiminto = false, nimettavat = false,
}: {
  /** 274 (T547, Toni 29.10.2026 klo 15.22): kuva näytetään nimellä, tai lajina ja päivänä — ei tiedostonimenä — ja sen voi nimetä
   *  («Nimeä»: nimi + laji kuvaus / sijainti / sijainti 3D). Tiedostonimi jää vihjeeksi. Muut kuin kuvat ennallaan. */
  nimettavat?: boolean;
  /** Toni 19.10.2026 klo 19.45: lisäysnappi päätoimintonapin tyylillä (nappi--paa), kosketusalue ≥ 44 px ja kamerakuvake tekstin edessä. */
  paatoiminto?: boolean;
  /** 250 (Toni 19.10.2026): liitteen laji valittavissa lisättäessä (esim. «allekirjoitettu pöytakirja»); tyhjä = tavallinen. */
  lajit?: "allekirjoitettu_poytakirja"[];
  /** Toni 19.10.2026 klo 11.50: lisäysnappi avaa tabletilla suoraan kameran (capture), ja galleriasta valinta on oma nappinsa. */
  kamera?: boolean;
  /** Lisäysnappi isona (sormella osuttava). */
  iso?: boolean;
  /** Kuvat pienoiskuvina, napautus avaa ison kuvan samalle sivulle (Toni 17.10.2026 klo 07.40 kohta 2, punch-kuvat). */
  pienoiskuvat?: boolean;
  kohdeTaulu: string;
  kohdeId: number;
  /**
   * Saako lisätä liitteen. Vastaa `POST /api/liitteet`.
   *
   * Nimi on historiallinen; kutsupaikkoja on kymmenkunta eikä
   * uudelleennimeäminen kuulu tähän korjaukseen.
   */
  saaMuokata: boolean;
  /**
   * Saako poistaa liitteen. Vastaa `DELETE /api/liitteet/:id`, joka on
   * tiukempi kuin lisäys: `paakayttaja`, `suunnittelija`.
   *
   * Oli sama lippu kuin lisäyksellä, ja siitä seurasi että hitsauksen
   * työnjohtaja näki poistonapin jonka API hylkää 403:lla. Kaksi eri
   * oikeutta ei jaa lippua vaikka roolilista sattuisi olemaan sama
   * (PAATOKSET §Oikeuslipun nimi on osa oikeustarkistusta).
   *
   * Oletus on `saaMuokata`, jolloin vanhat kutsupaikat käyttäytyvät
   * ennallaan — ne ovat kaikki paikkoja joissa listat ovat samat.
   */
  saaPoistaa?: boolean;
  otsikko?: string;
  /** Kaikki tämän paneelin liitteet arkaluonteisiksi (palkkaoikeus). */
  arkaluonteinenOletus?: boolean;
  /** Toni 16.10.2026 klo 04.15: liitteen voi merkitä käsin arkaluonteiseksi (esim. todistus jossa henkilötunnus).
   *  Ei oletus — pätevyystodistus on tavallinen työasiakirja. */
  voiMerkitaArkaluonteiseksi?: boolean;
  /**
   * Kutsutaan kun liitteitä lisätään tai poistetaan.
   *
   * Liite voi muuttaa kohteen tilaa — pätevyydellä se merkitsee
   * todennetuksi — eikä isäntänäkymä tiedä siitä muuten.
   */
  muuttui?: () => void | Promise<void>;
  /** Lisäysnapin teksti (oletus «Lisää liite»). Selaimen oma «Valitse tiedostot / Ei valittua tiedostoa» ei näy (Toni 12.10.2026). */
  lisaaTeksti?: string;
  /** Lyhyt ohje lisäysnapin alla, esim. «Lisää tarkastuspöytäkirja kuvana tai PDF:nä.» */
  vihje?: string;
  /** Poistonapin teksti — pöytäkirjan liite arkistoidaan eikä poisteta (233), joten «Arkistoi». */
  poistaTeksti?: string;
}) {
  const [liitteet, setLiitteet] = useState<Liite[]>([]);
  const [ladataan, setLadataan] = useState(true);
  const [lahetetaan, setLahetetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const syote = useRef<HTMLInputElement>(null);
  const kamerasyote = useRef<HTMLInputElement>(null);
  const [merkitty, setMerkitty] = useState(false);
  const [laji, setLaji] = useState<"" | "allekirjoitettu_poytakirja">("");
  const [nimeaa, setNimeaa] = useState<{ id: number; nimi: string; kuvalaji: string } | null>(null);
  /* Toni 4.11.2026 klo 16.30 (A): avattu liite arkistoidaan «Poista»-napista. Palvelin palauttaa arkistoidut vain pääkäyttäjälle,
     ja ne näkyvät vasta «Näytä arkistoidut» -valinnalla kuten kohteissa; muille valintaa ei piirretä, koska arkistoituja ei tule. */
  const [naytaArkistoidut, setNaytaArkistoidut] = useState(false);
  const arkistoituja = liitteet.filter((l) => l.arkistoitu).length;
  const naytettavat = naytaArkistoidut ? liitteet : liitteet.filter((l) => !l.arkistoitu);

  const lataa = useCallback(async () => {
    setLadataan(true);
    try {
      setLiitteet(await api.liitteet(kohdeTaulu, kohdeId, true));
      setVirhe(null);
    } catch (e) {
      // Hiljainen catch piilotti aiemmin sen, ettei moduuli ollut
      // käytössä tai oikeus puuttui — käyttäjälle näkyi vain "ei
      // liitteitä", eikä mikään kertonut miksi lataus ei toimi.
      setLiitteet([]);
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.haku_virhe"));
    } finally { setLadataan(false); }
  }, [kohdeTaulu, kohdeId]);

  useEffect(() => { void lataa(); }, [lataa]);

  async function laheta(tiedostot: FileList | null) {
    if (!tiedostot?.length) return;
    setLahetetaan(true);
    setIlmoitus(null);
    let onnistui = 0;

    for (const tiedosto of Array.from(tiedostot)) {
      try {
        const sisalto = await lueBase64(tiedosto);
        await api.lisaaLiite({
          kohde_taulu: kohdeTaulu, kohde_id: kohdeId,
          nimi: tiedosto.name, sisalto,
          arkaluonteinen: arkaluonteinenOletus || merkitty,
          ...(laji && { laji }),
        });
        onnistui++;
      } catch (e) {
        // Virhe näytetään aina, myös kun se ei ole ApiError: tyhjä
        // reaktio valinnan jälkeen on pahin mahdollinen palaute.
        setIlmoitus(
          e instanceof ApiError
            ? `${tiedosto.name}: ${virheTeksti(e.virhe, e.message)}`
            : `${tiedosto.name}: ${t("liite.lahetys_virhe")} ` +
              (e instanceof Error ? e.message : "")
        );
        break;
      }
    }

    if (onnistui > 0) {
      setIlmoitus(onnistui === 1 ? t("liite.tallennettu") : t("liite.tallennettu_n", { n: onnistui }));
      await lataa();
      await muuttui?.();
    }
    if (syote.current) syote.current.value = "";
    if (kamerasyote.current) kamerasyote.current.value = "";
    setLaji("");
    setLahetetaan(false);
  }

  // Toni 16.10.2026 klo 04.25: merkintä jälkikäteen (kuka tahansa liitteen lisääjä), poisto vain palkkaoikeudella.
  async function merkitse(id: number, arvo: boolean) {
    try {
      await api.merkitseLiite(id, arvo);
      setIlmoitus(arvo ? t("liite.merkitty_arkaluonteiseksi") : t("liite.merkinta_poistettu"));
      await lataa();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe"));
    }
  }

  async function tallennaNimi() {
    if (!nimeaa) return;
    try {
      await api.nimeaKuva(nimeaa.id, { nimi: nimeaa.nimi, kuvalaji: nimeaa.kuvalaji || null });
      setNimeaa(null);
      setIlmoitus(t("liite.nimi_tallennettu"));
      await lataa();
      await muuttui?.();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.lahetys_virhe"));
    }
  }

  async function poista(id: number, nimi: string) {
    if (!window.confirm(t("liite.poista_kysymys", { nimi }))) return;
    try {
      const r = await api.poistaLiite(id);
      setIlmoitus(r.arkistoitu ? t("liite.arkistoitu_ilmoitus") : t("liite.poistettu"));
      await lataa();
      await muuttui?.();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe"));
    }
  }

  async function palauta(id: number) {
    try {
      await api.palautaLiite(id);
      setIlmoitus(t("liite.palautettu_ilmoitus"));
      await lataa();
      await muuttui?.();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe"));
    }
  }

  return (
    <div className="liitteet">
      <h3>{otsikko} {naytettavat.length > 0 && <span className="dim">({naytettavat.length})</span>}</h3>
      {arkistoituja > 0 && (
        <label className="dim" data-liite-nayta-arkistoidut={arkistoituja}>
          <input type="checkbox" checked={naytaArkistoidut} onChange={(e) => setNaytaArkistoidut(e.target.checked)} />{" "}
          {t("liite.nayta_arkistoidut", { n: arkistoituja })}
        </label>
      )}

      {ladataan && <p className="dim">{t("ltar.ladataan")}</p>}

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {!ladataan && !virhe && naytettavat.length === 0 && (
        <p className="dim">{t("liite.ei_liitteita")}</p>
      )}

      {naytettavat.length > 0 && (
        <ul className="liitelista">
          {naytettavat.map((l) => {
            const nimetty = nimettavat && onKuva(l.mime);
            const naytto = nimetty ? kuvanNimi(l) : l.alkuperainen;
            return (
            <li key={l.id} data-liite={l.id} data-liite-arkistoitu={l.arkistoitu ? "" : undefined}>
              {pienoiskuvat && onKuva(l.mime) && <Pienoiskuva id={l.id} nimi={naytto} />}
              <a href={api.liiteOsoite(l.id)} target="_blank" rel="noreferrer"
                 className="liite__linkki" title={nimetty ? t("liite.tiedostonimi_title", { nimi: l.alkuperainen }) : undefined}>
                <span className="liite__kuvake" aria-hidden="true">
                  {l.mime === "application/pdf" ? "PDF" : t("liite.kuva_merkki")}
                </span>
                <span className="liite__nimi" data-liite-naytto={l.id}>{naytto}</span>
              </a>
              {nimetty && saaMuokata && nimeaa?.id !== l.id && (
                <button type="button" className="nappi nappi--kevyt" data-liite-nimea={l.id}
                        onClick={() => setNimeaa({ id: l.id, nimi: l.nimi ?? "", kuvalaji: l.kuvalaji ?? "" })}>{t("liite.nimea")}</button>
              )}
              {nimetty && nimeaa?.id === l.id && (
                <span className="liite__nimeys" data-liite-nimeys={l.id}>
                  <label>{t("liite.kuvan_nimi")}{" "}
                    <input type="text" maxLength={200} value={nimeaa.nimi} placeholder={t("liite.kuvan_nimi_paikka")} data-liite-nimi-kentta={l.id}
                           onChange={(e) => setNimeaa({ ...nimeaa, nimi: e.target.value })} />
                  </label>{" "}
                  <label>{t("liite.kuvalaji_valinta")}{" "}
                    <select value={nimeaa.kuvalaji} data-liite-kuvalaji={l.id} onChange={(e) => setNimeaa({ ...nimeaa, kuvalaji: e.target.value })}>
                      <option value="">{t("liite.kuvalaji.ei")}</option>
                      {KUVALAJIT.map((k) => <option key={k} value={k}>{t(`liite.kuvalaji.${k}` as KaannosAvain)}</option>)}
                    </select>
                  </label>{" "}
                  <button type="button" className="nappi" data-liite-nimi-tallenna={l.id} onClick={() => void tallennaNimi()}>{t("liite.tallenna_nimi")}</button>{" "}
                  <button type="button" className="nappi nappi--kevyt" onClick={() => setNimeaa(null)}>{t("liite.peru_nimi")}</button>
                </span>
              )}
              {l.laji && <span className="merkki" data-liite-laji={l.laji}>{t(`liite.laji.${l.laji}` as KaannosAvain)}</span>}
              {l.arkistoitu && <span className="merkki">{t("liite.arkistoitu_merkki")}</span>}
              {/* T701 (Toni 5.11.2026 klo 14.50): «Palauta arkistosta». Arkistoituja tulee palvelimelta vain pääkäyttäjälle, ja reitti on
                  vain pääkäyttäjän, joten nappi piirtyy jokaiselle näkyvälle arkistoidulle liitteelle. */}
              {l.arkistoitu && (
                <button type="button" className="nappi nappi--kevyt" data-liite-palauta={l.id} title={t("liite.palauta_title")}
                        onClick={() => void palauta(l.id)}>{t("liite.palauta")}</button>
              )}
              <span className="liite__tiedot mono">
                {koko(l.koko)}
                {l.arkaluonteinen && (
                  <span className="liite__suojattu" title={t("liite.suojattu_title")}>
                    {t("liite.suojattu")}
                  </span>
                )}
              </span>
              {saaMuokata && !l.arkaluonteinen && (
                <button className="nappi nappi--kevyt" data-liite-merkitse={l.id}
                        onClick={() => void merkitse(l.id, true)}>{t("liite.merkitse_nappi")}</button>
              )}
              {saaMuokata && l.arkaluonteinen && l.saa_poistaa_merkinnan && (
                <button className="nappi nappi--kevyt" data-liite-poista-merkinta={l.id}
                        onClick={() => void merkitse(l.id, false)}>{t("liite.poista_merkinta_nappi")}</button>
              )}
              {/* Toni 5.11.2026 klo 07.55 kohta 1: nappi näkyy vain, jos rajapinta sallii (saa_poistaa samasta säännöstä kuin poistoreitti). */}
              {saaPoistaa && l.saa_poistaa !== false && !l.arkistoitu && (
                <button className="nappi nappi--kevyt" data-liite-poista={l.id}
                        onClick={() => void poista(l.id, naytto)}>
                  {poistaTeksti ?? t("liite.poista")}
                </button>
              )}
            </li>
            );
          })}
        </ul>
      )}

      {saaMuokata && (
        <div className="liitteet__lisays">
          {/* Oma nappi selaimen tiedostokentän tilalla (Toni 12.10.2026 kuva: «Valitse tiedostot / Ei valittua tiedostoa»). Kenttä
              on piilossa mutta DOMissa, joten kävely voi yhä asettaa tiedoston. Puhelimessa valitsin tarjoaa kameran ja gallerian. */}
          {lajit && lajit.length > 0 && (
            <label className="dim" data-liite-laji-valinta="">{t("liite.laji_valinta")}{" "}
              <select value={laji} disabled={lahetetaan} onChange={(e) => setLaji(e.target.value as "" | "allekirjoitettu_poytakirja")}>
                <option value="">{t("liite.laji_tavallinen")}</option>
                {lajit.map((l) => <option key={l} value={l}>{t(`liite.laji.${l}` as KaannosAvain)}</option>)}
              </select>
            </label>
          )}
          {/* Toni 19.10.2026 klo 11.50: tabletilla painike avaa suoraan kameran (capture); galleria omana nappinaan. */}
          {kamera && (
            <>
              <button type="button" disabled={lahetetaan} onClick={() => kamerasyote.current?.click()} data-liite-kamera={kohdeTaulu}
                      className={["nappi", paatoiminto && "nappi--paa nappi--kuvake", iso && "nappi--iso"].filter(Boolean).join(" ")}>
                {paatoiminto && <KameraKuvake />}{lisaaTeksti ?? t("liite.lisaa_nappi")}</button>
              <input hidden ref={kamerasyote} type="file" accept="image/*" capture="environment"
                     onChange={(e) => void laheta(e.target.files)} disabled={lahetetaan} />
            </>
          )}
          <button type="button" className={iso && !kamera ? "nappi nappi--iso" : kamera ? "nappi nappi--kevyt" : "nappi"} disabled={lahetetaan}
                  onClick={() => syote.current?.click()} data-liite-lisaa={kohdeTaulu}>
            {kamera ? t("liite.galleriasta") : (lisaaTeksti ?? t("liite.lisaa_nappi"))}</button>
          <input
            hidden
            ref={syote} type="file" multiple
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            // capture puuttuu tarkoituksella: se pakottaisi kameran ja
            // estäisi valmiin kuvan valitsemisen galleriasta.
            onChange={(e) => void laheta(e.target.files)}
            disabled={lahetetaan}
          />
          {lahetetaan && <span className="dim">{t("liite.lahetetaan")}</span>}
          {voiMerkitaArkaluonteiseksi && !arkaluonteinenOletus && (
            <label className="dim" data-liite-merkitse-arkaluonteinen="">
              <input type="checkbox" checked={merkitty} onChange={(e) => setMerkitty(e.target.checked)} disabled={lahetetaan} />
              {" "}{t("liite.merkitse_arkaluonteinen")}
            </label>
          )}
        </div>
      )}

      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

      {saaMuokata && vihje && <p className="ikkuna__vihje" data-liite-vihje="">{vihje}</p>}

      {saaMuokata && arkaluonteinenOletus && (
        <p className="ikkuna__vihje">
          {t("liite.henkilotieto_vihje")}
        </p>
      )}
    </div>
  );
}

/* ── apurit ─────────────────────────────────────────────── */

/** Kamerakuvake napin tekstin eteen (Toni 19.10.2026 klo 19.45). Väri tekstistä (currentColor), ei uutta väriä. */
export const KameraKuvake = () => (
  <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" data-kuvake="kamera">
    {/* Kaaret pilkuin: välilyönnein «1 1 0 0 1» näyttää kommitoi.sh:n cron-portille kiinteältä tunnilta. */}
    <path d="M4,8h3l2-3h6l2,3h3a1,1,0,0,1,1,1v10a1,1,0,0,1-1,1H4a1,1,0,0,1-1-1V9a1,1,0,0,1,1-1z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);

/** Tiedosto base64-merkkijonoksi. data:-etuliite jää mukaan; palvelin poistaa sen. */
export const lueBase64 = (tiedosto: File): Promise<string> =>
  new Promise((valmis, virhe) => {
    const lukija = new FileReader();
    lukija.onload = () => valmis(String(lukija.result));
    lukija.onerror = () => virhe(new Error(t("liite.luku_virhe")));
    lukija.readAsDataURL(tiedosto);
  });

const koko = (tavut: number) =>
  tavut < 1024 * 1024
    ? `${Math.round(tavut / 1024)} kt`
    : `${luvut(tavut / 1024 / 1024, 1)} Mt`;
